package nl.appstuff.padelchecker.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import nl.appstuff.padelchecker.site.PageSeo
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.components.NavBar
import nl.appstuff.padelchecker.site.localization.LocalSiteLanguage
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import nl.appstuff.padelchecker.site.components.sections.FooterSection
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.RequestInit

private const val API_BASE_URL = "https://api.padelchecker.nl"

@Page
@Composable
fun ContactPage() {
    val seoStrings = LocalSiteStrings.current
    val language = LocalSiteLanguage.current
    PageSeo(
        title = seoStrings.contactSeoTitle,
        description = seoStrings.contactSeoDescription,
        canonicalPath = "/contact",
        lang = language.tag,
    )

    Column(modifier = Modifier.fillMaxWidth().minHeight(100.vh)) {
        NavBar()
        ContactContent()
        FooterSection()
    }
}

@Composable
private fun ContactContent() {
    val strings = LocalSiteStrings.current
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var subject by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var success by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("flex", "1") }
            .background(SiteColors.backgroundLight)
            .padding(topBottom = 64.px, leftRight = 24.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().maxWidth(760.px),
            verticalArrangement = Arrangement.spacedBy(32.px),
        ) {
            SpanText(
                strings.contactTitle,
                modifier = Modifier
                    .fontSize(40.px)
                    .fontWeight(FontWeight.Bold)
                    .color(SiteColors.textLight),
            )
            SpanText(
                strings.contactIntro,
                modifier = Modifier
                    .fontSize(16.px)
                    .lineHeight(1.7)
                    .color(SiteColors.textMutedLight),
            )

            if (success) {
                Div(attrs = {
                    style {
                        backgroundColor(rgb(0xE6, 0xF4, 0xEC))
                        borderRadius(8.px)
                        padding(16.px)
                    }
                }) {
                    SpanText(
                        strings.contactSuccess,
                        modifier = Modifier
                            .fontSize(15.px)
                            .color(SiteColors.green),
                    )
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(20.px), modifier = Modifier.fillMaxWidth()) {
                    ContactField(label = strings.contactFieldName) {
                        Input(InputType.Text) {
                            value(name)
                            onInput { name = it.value }
                            attr("placeholder", strings.contactPlaceholderName)
                            if (loading) attr("disabled", "")
                            applyInputStyle()
                        }
                    }
                    ContactField(label = strings.contactFieldEmail) {
                        Input(InputType.Email) {
                            value(email)
                            onInput { email = it.value }
                            attr("placeholder", strings.contactPlaceholderEmail)
                            if (loading) attr("disabled", "")
                            applyInputStyle()
                        }
                    }
                    ContactField(label = strings.contactFieldSubject) {
                        Input(InputType.Text) {
                            value(subject)
                            onInput { subject = it.value }
                            attr("placeholder", strings.contactPlaceholderSubject)
                            if (loading) attr("disabled", "")
                            applyInputStyle()
                        }
                    }
                    ContactField(label = strings.contactFieldMessage) {
                        TextArea(
                            value = message,
                            attrs = {
                                onInput { message = it.value }
                                attr("placeholder", strings.contactPlaceholderMessage)
                                attr("rows", "6")
                                if (loading) attr("disabled", "")
                                applyInputStyle()
                                style {
                                    property("resize", "vertical")
                                }
                            },
                        )
                    }

                    if (errorMessage.isNotEmpty()) {
                        Div(attrs = {
                            style {
                                fontSize(14.px)
                                color(rgb(0xD9, 0x30, 0x25))
                            }
                        }) {
                            Text(errorMessage)
                        }
                    }

                    Button(attrs = {
                        if (loading) attr("disabled", "")
                        onClick {
                            if (name.isBlank() || email.isBlank() || subject.isBlank() || message.isBlank()) {
                                errorMessage = strings.contactErrorFillAll
                                return@onClick
                            }
                            errorMessage = ""
                            loading = true
                            scope.launch {
                                submitContactForm(name, email, subject, message)
                                    .onSuccess { success = true }
                                    .onFailure { errorMessage = strings.contactErrorSendFailed }
                                loading = false
                            }
                        }
                        style {
                            if (loading) {
                                backgroundColor(rgba(26, 115, 232, 0.6))
                            } else {
                                backgroundColor(rgb(0x1A, 0x73, 0xE8))
                            }
                            color(Color.white)
                            property("border", "none")
                            borderRadius(999.px)
                            padding(12.px, 28.px)
                            fontSize(16.px)
                            property("font-weight", "600")
                            property("cursor", if (loading) "not-allowed" else "pointer")
                            property("align-self", "flex-start")
                            property("font-family", "'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif")
                        }
                    }) {
                        Text(if (loading) strings.contactSubmitting else strings.contactSubmit)
                    }
                }
            }
        }
    }
}

@Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
private suspend fun submitContactForm(
    name: String,
    email: String,
    subject: String,
    message: String,
): Result<Unit> = runCatching {
    val bodyStr = buildJsonBody(email, name, subject, message)
    val init: dynamic = js("{}")
    init.method = "POST"
    init.body = bodyStr
    val headers: dynamic = js("{}")
    headers["Content-Type"] = "application/json"
    init.headers = headers
    val response = window.fetch("$API_BASE_URL/api/contact", init as RequestInit).await()
    if (!response.ok) error("Server responded with ${response.status}")
}

private fun buildJsonBody(email: String, name: String, subject: String, message: String): String {
    fun String.esc() = replace("\\", "\\\\")
        .replace("\"", "\\\"")
        .replace("\n", "\\n")
        .replace("\r", "\\r")
    return """{"email":"${email.esc()}","name":"${name.esc()}","subject":"${subject.esc()}","message":"${message.esc()}"}"""
}

@Composable
private fun ContactField(label: String, content: @Composable () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(6.px), modifier = Modifier.fillMaxWidth()) {
        SpanText(
            label,
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(14.px)
                .fontWeight(FontWeight.Medium)
                .color(SiteColors.textLight),
        )
        content()
    }
}

private fun AttrsScope<*>.applyInputStyle() {
    style {
        width(100.percent)
        padding(12.px, 14.px)
        fontSize(15.px)
        color(rgb(0x1E, 0x1E, 0x1E))
        backgroundColor(Color.white)
        property("border", "1px solid #D0D0D0")
        borderRadius(8.px)
        property("box-sizing", "border-box")
        property("outline", "none")
        property("font-family", "'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif")
    }
}
